import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    mdiAlertCircleOutline,
    mdiCheckCircleOutline,
    mdiDomain,
} from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { type ResidentComposerPayload } from './ResidentComposer'
import { ResidentThreadShell } from './ResidentThreadShell'
import { enterConversation } from './enter-conversation'
import { useResidentIntakeState, useSendResidentIntakeMessage } from '../../hooks/useResidentIntake'

/**
 * ResidentIntakePage is what someone lands on from an intake address printed
 * in an AG convocation or pinned on a notice board (spec 2026-09-11-11).
 *
 * Modelled on PublicLabelPage, and for the same reasons:
 *
 *  - Mobile first, one column, large touch targets. It is read standing in a
 *    hall, on a phone, often in poor light.
 *  - No app chrome, no navigation, no organization context — `/r/` is in
 *    isExternalCounterpartyRoute() so it renders under BareShell. The reader
 *    has no account and nowhere else to go.
 *  - The payload is an explicit allow-list from the server: the organization's
 *    display name and the name of the equipment or site the address is about.
 *    Nothing else — this URL is printed on a board anyone can read.
 *  - An address that is not label-backed (the desk queue, the voice surface, a
 *    generated token) arrives here as the same "unknown" state as one that
 *    never existed. That is the server's decision, not this page's.
 *
 * Since spec 2026-09-18-03 it is two steps, like the QR door: a way back, then
 * the STANDARD composer — the same component the writer will use for every
 * message after this one, so a photo, a file and a recording can ride with the
 * first one. Nothing is persisted before that send: the thread is born with the
 * message, and a page opened out of curiosity leaves no row behind.
 */
export function ResidentIntakePage({ orgCode, address }: { orgCode: string; address: string }) {
    const { t } = useTranslation('conversations')
    const { data: view, isLoading, error } = useResidentIntakeState(orgCode, address)

    if (isLoading) {
        return (
            <div className="mx-auto max-w-md p-6 text-muted-foreground" data-testid="resident-intake-loading">
                {t('intake_page.loading')}
            </div>
        )
    }

    if (error || !view) {
        return (
            <div className="mx-auto max-w-md space-y-6 p-6" data-testid="resident-intake-not-found">
                <AppIcon tone="muted" path={mdiAlertCircleOutline} size="xl" className="mb-3" />
                <h1 className="text-xl font-semibold text-foreground">{t('intake_page.not_found_title')}</h1>
                <p className="mt-2 text-sm text-muted-foreground">{t('intake_page.not_found_body')}</p>
                <p className="mt-6 font-mono text-xs text-muted-foreground">{address}</p>
            </div>
        )
    }

    // A closed address is the one failure the server does distinguish: it was
    // printed on paper and cannot be recalled, so "unknown address" would be a
    // lie the reader can disprove by looking at the convocation in their hand.
    // Not an error state — nothing went wrong, the door is simply shut.
    if (view.closed) {
        return (
            <div className="mx-auto max-w-md space-y-4 p-6" data-testid="resident-intake-closed">
                <IntakeIdentity view={view} />
                <h2 className="text-lg font-semibold text-foreground" data-testid="resident-intake-closed-title">
                    {t('intake_page.closed_title')}
                </h2>
                <p className="text-sm text-muted-foreground" data-testid="resident-intake-closed-body">
                    {t('intake_page.closed_body')}
                </p>
            </div>
        )
    }

    return <OpenAddress view={view} orgCode={orgCode} address={address} />
}

/**
 * A live printed address: a way back, then the conversation.
 *
 * The step lives HERE rather than inside the form because step two is not a
 * section of this page any more — it is the conversation window itself (spec
 * 2026-09-18-05), full height, header and composer included, the very shell the
 * writer lands in one send later.
 */
function OpenAddress({
    view,
    orgCode,
    address,
}: {
    view: IntakeView
    orgCode: string
    address: string
}) {
    const [step, setStep] = useState<'contact' | 'compose'>('contact')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')

    if (step === 'compose') {
        return <IntakeComposer view={view} orgCode={orgCode} address={address} email={email} phone={phone} />
    }

    return (
        <div className="mx-auto max-w-md space-y-6 p-4 pb-16 md:p-6" data-testid="resident-intake-page">
            <IntakeIdentity view={view} />
            <ContactStep
                email={email}
                phone={phone}
                setEmail={setEmail}
                setPhone={setPhone}
                onContinue={() => setStep('compose')}
            />
        </div>
    )
}

interface IntakeView {
    org_display_name: string
    asset_name?: string
}

function IntakeIdentity({ view }: { view: { org_display_name: string; asset_name?: string } }) {
    const { t } = useTranslation('conversations')

    return (
        <div data-testid="resident-intake-header">
            <p className="text-xs uppercase tracking-wide text-muted-foreground" data-testid="resident-intake-org">
                {view.org_display_name}
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-foreground" data-testid="resident-intake-title">
                {t('intake_page.title')}
            </h1>
            {view.asset_name && (
                <p className="mt-3 flex items-center gap-1.5 text-sm text-foreground">
                    <AppIcon tone="muted" path={mdiDomain} size="sm" className="shrink-0" />
                    <span data-testid="resident-intake-asset">{view.asset_name}</span>
                </p>
            )}
        </div>
    )
}

function ContactStep({
    email,
    phone,
    setEmail,
    setPhone,
    onContinue,
}: {
    email: string
    phone: string
    setEmail: (value: string) => void
    setPhone: (value: string) => void
    onContinue: () => void
}) {
    const { t } = useTranslation('conversations')

    return (
        <form
            className="space-y-4"
            data-testid="resident-intake-form"
            onSubmit={event => {
                event.preventDefault()
                onContinue()
            }}
        >
            <h2 className="text-lg font-semibold text-foreground">{t('intake_page.contact_title')}</h2>
            <p className="text-sm text-muted-foreground" data-testid="resident-intake-intro">
                {t('intake_page.contact_intro')}
            </p>

            {/* Two typed fields rather than one free-text box: a phone typed
                into a field the ledger reads as an e-mail is normalised as
                one, and the writer is then unreachable. */}
            <div className="space-y-1.5">
                <Label htmlFor="resident-intake-email">{t('intake_page.email_label')}</Label>
                <Input
                    id="resident-intake-email"
                    type="email"
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    placeholder={t('intake_page.email_placeholder')}
                    data-testid="resident-intake-email"
                />
            </div>

            <div className="space-y-1.5">
                <Label htmlFor="resident-intake-phone">{t('intake_page.phone_label')}</Label>
                <Input
                    id="resident-intake-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phone}
                    onChange={event => setPhone(event.target.value)}
                    placeholder={t('intake_page.phone_placeholder')}
                    data-testid="resident-intake-phone"
                />
                <p className="text-xs text-muted-foreground">{t('intake_page.contact_help')}</p>
            </div>

            <Button type="submit" size="lg" className="w-full" data-testid="resident-intake-continue">
                {t('intake_page.contact_continue')}
            </Button>
            {/* The anonymous path, said out loud rather than left to be
                guessed from two empty fields. */}
            <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                    setEmail('')
                    setPhone('')
                    onContinue()
                }}
                data-testid="resident-intake-skip-contact"
            >
                {t('intake_page.contact_skip')}
            </Button>
        </form>
    )
}

/**
 * Step two: the conversation window, before the conversation exists — the SAME
 * shell `/c/` renders (spec 2026-09-18-05), with an empty message list, no
 * status badge and no reference, because there is no thread to name yet.
 */
function IntakeComposer({
    view,
    orgCode,
    address,
    email,
    phone,
}: {
    view: IntakeView
    orgCode: string
    address: string
    email: string
    phone: string
}) {
    const { t } = useTranslation('conversations')
    const send = useSendResidentIntakeMessage(orgCode, address)

    const submit = useCallback(async (payload: ResidentComposerPayload) => {
        const result = await send.mutateAsync({
            body: payload.body,
            contact_email: email.trim() || undefined,
            contact_phone: phone.trim() || undefined,
            files: payload.files,
            voice: payload.voice,
        })
        // The page becomes the thread (spec 2026-09-18-03 § 5).
        enterConversation(result.conversation_url ?? '')
    }, [send, email, phone])

    // Reached only when the send succeeded and no thread came back with it.
    if (send.isSuccess && !send.data?.conversation_url) {
        return (
            <div className="mx-auto max-w-md space-y-6 p-4 pb-16 md:p-6" data-testid="resident-intake-acknowledged">
                <AppIcon path={mdiCheckCircleOutline} size="xl" className="mb-3" />
                <h2 className="text-lg font-semibold text-foreground">{t('intake_page.thanks_title')}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{t('intake_page.thanks_body')}</p>
            </div>
        )
    }

    const status = (send.error as { status?: number } | null)?.status
    const error = !send.isError
        ? null
        : status === 410
          ? t('intake_page.closed_body')
          : status === 429
            ? t('intake_page.rate_limited')
            : status === 413 || status === 422
              ? t('intake_page.too_heavy')
              : t('intake_page.error')

    return (
        <ResidentThreadShell
            organizationName={view.org_display_name}
            title={view.asset_name || t('intake_page.title')}
            empty
            emptyIntro={t('intake_page.intro')}
            pageTestId="resident-intake-compose"
            composer={{
                onSend: submit,
                sending: send.isPending,
                error,
                placeholder: t('intake_page.message_placeholder'),
                testIdPrefix: 'resident-intake',
                bodyTestId: 'resident-intake-body',
                errorTestId: 'resident-intake-error',
                voiceTestIdPrefix: 'resident-intake-voice',
            }}
        />
    )
}
